import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { CANADIAN_CITIES, BUSINESS_CATEGORIES } from '../lib/constants';
import { FIELD_INPUT, FIELD_LABEL, ImageUploader, SubmitButton, PremiumGate } from '../components/submitHelpers';

export default function SubmitBusiness() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    category: '',
    city: '',
    address: '',
    description: '',
    images: [],
    website_url: '',
    phone: '',
    tags: [],
  });

  function update(k, v) { setForm({ ...form, [k]: v }); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    if (!form.category) return toast.error('Select a category');
    if (!form.city) return toast.error('Select a city');
    if (form.description.trim().length < 30) return toast.error('Description must be at least 30 characters');

    setLoading(true);
    try {
      await api('/submissions/business', {
        method: 'POST',
        body: JSON.stringify({
          ...form,
          name: form.name.trim(),
          description: form.description.trim(),
          address: form.address.trim() || null,
          website_url: form.website_url.trim() || null,
          phone: form.phone.trim() || null,
        }),
      });
      toast.success('Submitted for review!');
      navigate('/app/my-submissions');
    } catch (err) {
      toast.error(err.message || 'Failed');
    } finally {
      setLoading(false);
    }
  }

  if (!user?.is_premium) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8">
        <BackBar to="/app/submit" />
        <PremiumGate message="Submitting a business is a Premium-only feature." />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6" data-testid="submit-business-page">
      <BackBar to="/app/submit" />
      <h1 className="text-3xl font-serif font-bold text-[#2B2D42] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
        Submit a Business
      </h1>
      <p className="text-sm text-[#6D6875] mb-6">
        We'll review your submission within 48 hours.
      </p>

      <form onSubmit={handleSubmit} className="space-y-5" data-testid="submit-business-form">
        <div>
          <label className={FIELD_LABEL}>Business name *</label>
          <input data-testid="business-name-input" className={FIELD_INPUT} value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="e.g. Buka Restaurant" />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={FIELD_LABEL}>Category *</label>
            <select data-testid="business-category-select" className={FIELD_INPUT} value={form.category} onChange={(e) => update('category', e.target.value)}>
              <option value="">Choose...</option>
              {BUSINESS_CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <label className={FIELD_LABEL}>City *</label>
            <select data-testid="business-city-select" className={FIELD_INPUT} value={form.city} onChange={(e) => update('city', e.target.value)}>
              <option value="">Choose...</option>
              {CANADIAN_CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={FIELD_LABEL}>Address (optional)</label>
          <input data-testid="business-address-input" className={FIELD_INPUT} value={form.address} onChange={(e) => update('address', e.target.value)} placeholder="613 King St W, Toronto, ON" />
        </div>

        <div>
          <label className={FIELD_LABEL}>Description * <span className="text-xs font-normal text-[#B5B5B5]">(30+ characters)</span></label>
          <textarea data-testid="business-description-input" rows={4} maxLength={1000} className={FIELD_INPUT} value={form.description} onChange={(e) => update('description', e.target.value)} placeholder="Tell us what makes this business special..." />
          <div className="text-xs text-right text-[#B5B5B5] mt-1">{form.description.length}/1000</div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={FIELD_LABEL}>Phone</label>
            <input data-testid="business-phone-input" type="tel" className={FIELD_INPUT} value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="+14165550000" />
          </div>
          <div>
            <label className={FIELD_LABEL}>Website</label>
            <input data-testid="business-website-input" type="url" className={FIELD_INPUT} value={form.website_url} onChange={(e) => update('website_url', e.target.value)} placeholder="https://" />
          </div>
        </div>

        <ImageUploader images={form.images} setImages={(images) => update('images', images)} max={5} testIdPrefix="business-submission" />

        <SubmitButton loading={loading} testId="business-submit-button">
          <Send className="w-5 h-5" />
          Submit for Review
        </SubmitButton>
      </form>
    </div>
  );
}

function BackBar({ to }) {
  return (
    <Link
      to={to}
      data-testid="submit-back-button"
      className="inline-flex items-center gap-2 text-sm font-semibold text-[#6D6875] hover:text-[#C8553D] mb-4"
    >
      <ArrowLeft className="w-4 h-4" />
      Back
    </Link>
  );
}
